import { useEffect, useRef, useState } from 'react'
import styles from './ui.module.css'

/**
 * Small popup menu anchored to its trigger. Arrow keys move, Esc closes and
 * returns focus to the trigger.
 * @param {{ trigger: (props) => JSX.Element, items: Array<{label:string, onSelect:()=>void, danger?:boolean, disabled?:boolean}>, align?: 'start'|'end', side?: 'top'|'bottom' }} props
 */
export function Menu({ trigger, items, align = 'end', side = 'bottom' }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const itemRefs = useRef([])

  useEffect(() => {
    if (!open) return
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    itemRefs.current.find((el) => el && !el.disabled)?.focus()
    return () => document.removeEventListener('pointerdown', onPointer)
  }, [open])

  const onKeyDown = (event) => {
    const enabled = itemRefs.current.filter((el) => el && !el.disabled)
    const index = enabled.indexOf(document.activeElement)
    if (event.key === 'Escape') {
      setOpen(false)
      triggerRef.current?.focus()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      enabled[(index + 1) % enabled.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      enabled[(index - 1 + enabled.length) % enabled.length]?.focus()
    }
  }

  return (
    <div className={styles.menuRoot} ref={rootRef} onKeyDown={onKeyDown}>
      {trigger({
        ref: triggerRef,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        onClick: () => setOpen((v) => !v),
      })}
      {open ? (
        <div
          role="menu"
          className={`${styles.menu} ${side === 'top' ? styles.menuTop : ''}`}
          style={align === 'end' ? { right: 0 } : { left: 0 }}
        >
          {items.map((item, i) => (
            <button
              key={item.label}
              ref={(el) => {
                itemRefs.current[i] = el
              }}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className={`${styles.menuItem} ${item.danger ? styles.menuDanger : ''}`}
              onClick={() => {
                setOpen(false)
                item.onSelect()
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
